import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { ChoiceOption, MultipleChoiceQuestion } from '../profile-question.model';

/** Figma: IVY-HA 235:18313 (desktop) / 235:18232 (mobile). */
@Component({
  selector: 'app-multiple-choice-question',
  standalone: true,
  imports: [NgClass, NgFor, NgIf],
  template: `
    <div class="flex flex-col items-center gap-[38px]">
      <h2 class="max-w-[275px] text-center text-2xl font-bold leading-[30px] md:max-w-none">{{ question.title }}</h2>

      <div class="flex w-full flex-col gap-3" role="group" [attr.aria-label]="question.title">
        <button
          *ngFor="let option of question.options"
          type="button"
          role="checkbox"
          [attr.aria-checked]="isSelected(option)"
          [disabled]="isLimited(option)"
          class="flex w-full items-center gap-3 rounded px-6 py-5 text-left text-base leading-6 disabled:opacity-40"
          [ngClass]="isSelected(option) ? 'border-2 border-[#5D38F1] bg-[#DAD6FB] px-[23px] py-[19px]' : 'border border-black bg-white'"
          (click)="toggle(option)"
        >
          <img *ngIf="isSelected(option)" class="shrink-0" src="assets/profile-onboarding/checkbox-checked.svg" width="24" height="24" alt="" />
          <span *ngIf="!isSelected(option)" class="size-6 shrink-0 border-[1.5px] border-black bg-white"></span>
          <span class="flex-1">{{ option.label }}</span>
        </button>
      </div>

      <button
        type="button"
        class="flex h-[55px] w-full items-center justify-center rounded-full bg-black px-9 text-[15px] font-bold uppercase leading-5 tracking-[1.5px] text-white disabled:opacity-40"
        [disabled]="!selected.size"
        (click)="answer.emit(selectedValues())"
      >Next</button>
    </div>
  `
})
export class MultipleChoiceQuestionComponent {
  @Input({ required: true }) question!: MultipleChoiceQuestion;
  @Output() answer = new EventEmitter<string[]>();

  selected = new Set<string>();

  isSelected(option: ChoiceOption): boolean {
    return this.selected.has(option.value);
  }

  toggle(option: ChoiceOption) {
    if (this.selected.has(option.value)) {
      this.selected.delete(option.value);
      return;
    }
    if (option.exclusive) {
      this.selected.clear();
    } else {
      // Picking a regular option undoes an exclusive one like "I don't experience..."
      for (const other of this.question.options) {
        if (other.exclusive) this.selected.delete(other.value);
      }
    }
    this.selected.add(option.value);
  }

  /** At `maxSelections`, the other regular options can't be picked until one is unpicked. */
  isLimited(option: ChoiceOption): boolean {
    const max = this.question.maxSelections;
    return !!max && !option.exclusive && !this.selected.has(option.value) && this.selected.size >= max;
  }

  selectedValues(): string[] {
    // Keep the order the options are listed in
    return this.question.options.filter((o) => this.selected.has(o.value)).map((o) => o.value);
  }
}
